import axios from "axios";
import AddStaff from "Components/Admin-Components/Staff/AddStaff";
import React, { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MdAddTask } from "react-icons/md";
import { TiEdit, TiUserDeleteOutline } from "react-icons/ti";
import { BsPersonPlusFill } from "react-icons/bs";
import AssignTask from "Components/Admin-Components/Staff/AssignTask";
import noProfilePic from "../../assets/img/auth/noProfilePic.avif";
import ConfirmDeleteModal from "Components/Admin-Components/Common Modals/ConfirmDeleteModal";
import toast from "react-hot-toast";
import { IoIosArrowDropleft, IoIosArrowDropright } from "react-icons/io";
import { filter } from "@chakra-ui/system";
import EditStaff from "Components/Admin-Components/Staff/EditStaff";

function Staff() {
  //States for opening different modals
  const [addStaffModalOpen, setAddStaffModalOpen] = useState(false);
  const [editStaffModalOpen, setEditStaffModalOpen] = useState(false);
  const [assignTaskModalOpen, setAssignTaskModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [staffs, setStaffs] = useState([]); //State for all staffs
  const [filteredStaffs, setFilteredStaffs] = useState([]);
  const [loading, setLoading] = useState(false); // State for loader
  const [selectedStaff, setSelectedStaff] = useState({}); //State for selected staff
  const [searchTerm, setSearchTerm] = useState("");

  //State for pagination
  const [currentPage, setCurrentPage] = useState(1);

  //Fetching user details from the local storage
  const storedData = localStorage.getItem("Ananya Enterprize-Users-Data");

  //Converting the fetched user details into JSON
  const userData = storedData ? JSON.parse(storedData) : {};

  //Function to get all staffs
  const getAllStaffs = () => {
    setLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/user/get-all-staffs`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of getAllStaff: ", response.data);
        setStaffs(response.data.staffs);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllStaffs();
  }, []);

  //Function to delete a staff
  const deleteStaff = (id) => {
    setLoading(true);
    let config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/user/delete-user/${id}`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of deleteStaff: ", response.data);
        setLoading(false);
        setDeleteModalOpen(false);
        getAllStaffs();
        toast.success(response.data.message);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        toast.error("Error in deleting staff");
      });
  };

  //Function to handle click on action buttons
  const handleActionButtonClick = (staff, buttonName) => {
    setSelectedStaff(staff);
    if (buttonName === "assign task") setAssignTaskModalOpen(true);
    else if (buttonName === "edit staff") setEditStaffModalOpen(true);
    else if (buttonName === "delete staff") setDeleteModalOpen(true);
  };

  useEffect(() => {
    const staffsFiltered = staffs?.filter((staff) => {
      const matchesSearch = staff.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
    setFilteredStaffs(staffsFiltered);
  }, [staffs, searchTerm]);

  //Pagination Logics
  //Function to handle page change
  const handlePageChange = (newPage) => {
    if (newPage === 0) {
      setCurrentPage(1);
    } else if (newPage > totalPages) {
      setCurrentPage(totalPages);
    } else {
      setCurrentPage(newPage);
    }
  };

  const staffsPerPage = 10;
  const totalPages = Math.ceil(staffs.length / staffsPerPage);

  //Finding the tasks for current page
  const currentStaffs = filteredStaffs?.slice(
    (currentPage - 1) * staffsPerPage,
    currentPage * staffsPerPage
  );

  return (
    <div className="mx-2 my-6 md:my-8">
      {/* All Modals rendered here */}

      {/* Add Staff Modal */}
      {addStaffModalOpen && (
        <AddStaff
          onClose={() => setAddStaffModalOpen(false)}
          getAllStaffs={getAllStaffs}
        />
      )}

      {editStaffModalOpen && (
        <EditStaff
          staff={selectedStaff}
          onClose={() => setEditStaffModalOpen(false)}
          getAllStaffs={getAllStaffs}
        />
      )}

      {/* Assign Task Modal */}
      {assignTaskModalOpen && (
        <AssignTask
          id={userData._id}
          staff={selectedStaff}
          getAllStaffs={getAllStaffs}
          onClose={() => setAssignTaskModalOpen(false)}
        />
      )}

      {/* Delete Staff Modal */}
      {deleteModalOpen && (
        <ConfirmDeleteModal
          item="staff"
          deleteFunction={() => deleteStaff(selectedStaff._id)}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-wide text-primary ">
          Staffs
        </h1>
        <button
          className="flex h-fit items-center gap-1 rounded bg-primary px-2 py-1 text-sm font-bold tracking-wide text-white hover:bg-hoverPrimary"
          onClick={() => setAddStaffModalOpen(true)}
        >
          Add Staff <BsPersonPlusFill />
        </button>
      </div>

      <div className="my-6 flex flex-col gap-4">
        {/* Search Bar and other filters*/}
        <div className="relative flex items-center justify-between">
          <input
            type="text"
            placeholder="Search..."
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[100%] rounded-lg border border-gray-300 p-2 pl-8 pr-10 text-primaryText focus:outline-none focus:ring-1 focus:ring-blue-400 lg:w-auto"
          />
          <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 transform text-primary" />
        </div>

        {/* Staff table goes here */}
        <div className="min-h-[70vh] overflow-x-auto">
          <table className="min-w-full overflow-y-visible rounded-md border-blue-200 ">
            {/* Table Head */}
            <thead>
              <tr className="bg-blue-50">
                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Sl
                </th>

                <th className="px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Profile Pic
                </th>

                <th className="px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Name
                </th>

                <th className="px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Phone
                </th>

                <th className="px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Ongoing Task
                </th>

                <th className="rounded-tr-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Actions
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="text-[13px] font-medium tracking-wide text-[#202224]">
              {loading ? (
                //Skeleton Loader
                [...Array(10)].map((_, index) => (
                  <tr
                    key={index}
                    className="animate-pulse border-b border-gray-200"
                  >
                    <td className="px-6 py-4">
                      <div className="h-4  w-3/4 rounded bg-skeletonLoaderColor"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4  w-1/2 rounded bg-skeletonLoaderColor"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4  rounded bg-skeletonLoaderColor "></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4  w-1/4 rounded bg-skeletonLoaderColor"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4  w-1/4 rounded bg-skeletonLoaderColor"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4  w-1/4 rounded bg-skeletonLoaderColor"></div>
                    </td>
                  </tr>
                ))
              ) : currentStaffs?.length > 0 ? (
                currentStaffs?.map((staff, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {index + 1}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      <img
                        src={staff.profilePic || noProfilePic}
                        alt="Profile Pic"
                        className="h-8 w-8 rounded-full border border-primary"
                      />
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {staff.name}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {staff.phone}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {staff.tasks.filter(
                        (task) => task.status.toLowerCase() === "ongoing"
                      ).length > 0 ? (
                        staff.tasks
                          .filter(
                            (task) => task.status.toLowerCase() === "ongoing"
                          )
                          .map((task) => (
                            <div
                              className="font-bold text-green-500"
                              key={task._id}
                            >
                              {task.name}
                            </div>
                          ))
                      ) : (
                        <span>No ongoing tasks</span>
                      )}
                    </td>
                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      <div className="flex items-center gap-2">
                        <button>
                          <TiEdit
                            size={24}
                            title="Edit Staff"
                            className="cursor-pointer font-bold text-green-500 duration-300 hover:scale-105 hover:text-green-800"
                            onClick={() =>
                              handleActionButtonClick(staff, "edit staff")
                            }
                          />
                        </button>

                        <button>
                          <MdAddTask
                            size={22}
                            title="Assign Task"
                            className="cursor-pointer font-bold text-primary duration-300 hover:scale-105 hover:text-hoverPrimary"
                            onClick={() =>
                              handleActionButtonClick(staff, "assign task")
                            }
                          />
                        </button>

                        <button>
                          <TiUserDeleteOutline
                            size={24}
                            title="Delete Staff"
                            className="cursor-pointer font-bold text-red-500 duration-300 hover:scale-105 hover:text-red-800"
                            onClick={() =>
                              handleActionButtonClick(staff, "delete staff")
                            }
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="text-secondaryTextColor px-6 py-40 text-center text-lg"
                  >
                    No staffs available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="float-right my-4 flex items-center justify-between gap-2 font-medium">
          <span className="text-sm text-primaryText">
            {currentPage} - {totalPages} pages
          </span>
          <div className="flex items-center gap-2">
            <button
              className={`cursor-pointer rounded font-bold  ${
                currentPage === 1
                  ? "text-gray-300 hover:text-gray-300"
                  : "text-primary hover:text-hoverPrimary"
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <IoIosArrowDropleft size={24} />
            </button>

            <button
              className={`cursor-pointer rounded font-bold  ${
                currentPage === totalPages
                  ? "text-gray-300 hover:text-gray-300"
                  : "text-primary hover:text-hoverPrimary"
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <IoIosArrowDropright size={24} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Staff;
